/**
 * Goethe exams have no overall score; they use module-based pass/fail.
 * Use these helpers to show "2/3 modules passed" instead of "0/400" in history, dashboard, and analytics.
 * Section keys are normalized so e.g. "writing" and "schreiben" count as one module.
 */

const CANONICAL_SECTION = {
  lesen: 'lesen', reading: 'lesen',
  hören: 'hören', listening: 'hören',
  schreiben: 'schreiben', writing: 'schreiben',
  sprechen: 'sprechen', speaking: 'sprechen'
};

function canonicalSectionId(sectionId) {
  if (!sectionId) return '';
  const key = String(sectionId).toLowerCase();
  return CANONICAL_SECTION[key] ?? key;
}

/**
 * @param {Object} exam - Exam result object (from history or current result)
 * @returns {{ isGoethe: boolean, passed: number, total: number, displayShort: string, displayLong: string } | null}
 */
export function getGoetheModuleSummary(exam) {
  if (!exam) return null;
  const provider = (exam.provider || exam.examProvider || '').toUpperCase();
  if (provider !== 'GOETHE') return null;

  const sectionScores = exam.sectionScores || {};
  const canonicalTotal = new Set(Object.keys(sectionScores).map(canonicalSectionId).filter(Boolean));
  const total = canonicalTotal.size;

  const passedCanonical = new Set();
  for (const [id, data] of Object.entries(sectionScores)) {
    if (data && data.passed === true) passedCanonical.add(canonicalSectionId(id));
  }
  const passed = passedCanonical.size;

  return {
    isGoethe: true,
    passed,
    total,
    displayShort: total > 0 ? `${passed}/${total}` : ', ',
    displayLong: total > 0
      ? (passed === total
          ? `${passed} von ${total} Modulen bestanden`
          : `${passed} von ${total} Modulen bestanden`)
      : ', ',
    allPassed: exam.allModulesPassed === true
  };
}

/**
 * For Goethe, a "percentage" for charts can be (passed/total)*100. For non-Goethe, use overallScore/maxScore.
 * @param {Object} exam
 * @returns {number} 0–100 for display
 */
export function getExamDisplayPercentage(exam) {
  if (!exam) return 0;
  const goethe = getGoetheModuleSummary(exam);
  if (goethe && goethe.total > 0) {
    return Math.round((goethe.passed / goethe.total) * 100);
  }
  const score = exam.overallScore ?? exam.totalScore ?? 0;
  const max = exam.maxScore || 225;
  return max > 0 ? Math.round((score / max) * 100) : 0;
}
